import { useState, useEffect } from 'react'
import { motion, Reorder } from 'framer-motion'
import type { GameResult } from '../../../hooks/useGameState'

// Confetti effect function (same as DailyWord)
const triggerConfetti = () => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return
  }

  const canvas = document.createElement('canvas')
  canvas.style.position = 'fixed'
  canvas.style.top = '0'
  canvas.style.left = '0'
  canvas.style.width = '100%'
  canvas.style.height = '100%'
  canvas.style.pointerEvents = 'none'
  canvas.style.zIndex = '9999'
  document.body.appendChild(canvas)

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  canvas.width = window.innerWidth
  canvas.height = window.innerHeight

  const particles: Array<{
    x: number
    y: number
    vx: number
    vy: number
    color: string
    size: number
  }> = []

  const colors = ['#10b981', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899', '#ef4444']

  for (let i = 0; i < 50; i++) {
    particles.push({
      x: canvas.width / 2,
      y: canvas.height / 2,
      vx: (Math.random() - 0.5) * 10,
      vy: (Math.random() - 0.5) * 10 - 5,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: Math.random() * 5 + 3,
    })
  }

  let animationId: number
  const animate = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    particles.forEach((particle, index) => {
      particle.x += particle.vx
      particle.y += particle.vy
      particle.vy += 0.3

      ctx.fillStyle = particle.color
      ctx.beginPath()
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
      ctx.fill()

      if (
        particle.y > canvas.height ||
        particle.x < 0 ||
        particle.x > canvas.width
      ) {
        particles.splice(index, 1)
      }
    })

    if (particles.length > 0) {
      animationId = requestAnimationFrame(animate)
    } else {
      document.body.removeChild(canvas)
    }
  }

  animate()

  setTimeout(() => {
    if (canvas.parentNode) {
      document.body.removeChild(canvas)
    }
    if (animationId) {
      cancelAnimationFrame(animationId)
    }
  }, 3000)
}

interface RankRushProps {
  items: Array<{ id: string; label: string; value: number }>
  solutionOrder: string[]
  gameState?: GameResult
  onComplete: (result: GameResult) => void
  context?: string // Optional context hint (e.g., "population", "year", "rating")
}

export default function RankRush({ items, solutionOrder, gameState, onComplete, context }: RankRushProps) {
  const [orderedItems, setOrderedItems] = useState(items)
  const [isComplete, setIsComplete] = useState(gameState?.status === 'won' || gameState?.status === 'lost')
  const [hasChecked, setHasChecked] = useState(false)

  useEffect(() => {
    if (gameState?.status === 'won' || gameState?.status === 'lost') {
      setIsComplete(true)
      setHasChecked(true)
    }
  }, [gameState])

  const checkOrder = () => {
    if (isComplete) return

    setHasChecked(true)
    const currentOrder = orderedItems.map(item => item.id)
    const isCorrect = JSON.stringify(currentOrder) === JSON.stringify(solutionOrder)

    setIsComplete(true)
    if (isCorrect) {
      // Trigger confetti on win
      triggerConfetti()
    }
    onComplete({
      status: isCorrect ? 'won' : 'lost'
    })
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Rank Rush</h2>
        {isComplete && gameState?.status === 'won' && (
          <span className="text-green-600 font-semibold">✅ Correct!</span>
        )}
        {isComplete && gameState?.status === 'lost' && (
          <span className="text-red-600 font-semibold">❌ Try again tomorrow</span>
        )}
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
        <p className="text-gray-700 font-semibold mb-2">
          📊 Rank these items from highest to lowest {context || 'value'}
        </p>
        <p className="text-sm text-gray-600">
          {context 
            ? `The numbers represent ${context}. Drag and drop to reorder from highest to lowest!`
            : 'The numbers represent the values you need to compare. Drag and drop to reorder!'
          }
        </p>
      </div>

      {/* Draggable List */}
      {!isComplete ? (
        <Reorder.Group
          axis="y"
          values={orderedItems}
          onReorder={setOrderedItems}
          className="space-y-3 mb-6"
        >
          {orderedItems.map((item) => (
            <Reorder.Item
              key={item.id}
              value={item}
              whileDrag={{ scale: 1.05, cursor: 'grabbing' }}
              className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 cursor-grab active:cursor-grabbing hover:border-purple-400 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-gray-400 text-sm">⋮⋮</div>
                  <div>
                    <p className="font-semibold text-gray-900">{item.label}</p>
                    <p className="text-sm text-gray-500">Value: {item.value.toLocaleString()}</p>
                  </div>
                </div>
                <div className="text-gray-400">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                  </svg>
                </div>
              </div>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      ) : (
        // Show correct order after completion
        <div className="space-y-3 mb-6">
          {solutionOrder.map((id, index) => {
            const item = items.find(i => i.id === id)!
            const isCorrect = orderedItems.findIndex(i => i.id === id) === index
            return (
              <div
                key={id}
                className={`
                  border-2 rounded-lg p-4
                  ${isCorrect ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center font-bold
                      ${isCorrect ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}
                    `}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{item.label}</p>
                      <p className="text-sm text-gray-500">Value: {item.value.toLocaleString()}</p>
                    </div>
                  </div>
                  {isCorrect ? (
                    <span className="text-green-600">✓</span>
                  ) : (
                    <span className="text-red-600">✗</span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Check Button */}
      {!isComplete && (
        <div className="flex justify-center">
          <button
            onClick={checkOrder}
            disabled={hasChecked}
            className="px-8 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {hasChecked ? 'Already Checked' : 'Check Answer'}
          </button>
        </div>
      )}

      {isComplete && gameState?.status === 'won' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 text-center text-green-600 font-semibold"
        >
          🎉 Perfect ranking!
        </motion.div>
      )}
    </div>
  )
}
